// The addresses of a post's thumbnail.
//
// One hosted image, one address, every surface. The card used to be rebuilt in
// CSS wherever it appeared, so its layout had to survive whatever box it landed
// in — and in the list view it didn't. An <img> can't come apart.
//
// TWO ADDRESSES, ONE FILE. A card is composed once and hosted in storage, and
// the page knows its storage address ahead of time (`hostedCardUrl`) because
// the key is a pure function of the post. The browser asks storage FIRST — a
// CDN file, no function in the way — and only when that 404s (the card has not
// been composed yet) does it fall back to `postCardUrl`, the route that
// composes, uploads and answers with the bytes. Before this, every thumbnail
// on the index went through the route unconditionally: a lambda woke, HEAD-
// probed storage, and answered 302 — a second per card before the first byte.

import { roomForKind, CO_ROOMS, type CoRoom } from './theme';
import { postSeed } from '../brand/portal';
import { hostedHeroUrl } from '../brand/heroUrl';
import type { CompanyMedia, CompanyPost } from './types';

/**
 * Bump when the design changes, so hosted cards are regenerated rather than
 * reused. v2 (2026-08-09): solid ground, the real baked bloom, contrast-checked
 * ink. v3 (2026-09-08): the shared composition (lib/brand/postArt), netti
 * bottom-right, a clipped title and two lines of dek. v4 (2026-09-12): THE
 * PORTAL — the card is painted in the colour of the page it opens. v5
 * (2026-09-18): THE GROUND LAW — netti painted for the card's own ground off
 * the stock ground sheet (lib/organism/groundLaw), seated by its nucleus.
 */
export const CARD_VERSION = 'v5';

export interface CardAuthor {
  name: string;
  avatarUrl: string | null;
}

type CardPost = Pick<CompanyPost, 'slug'> & Partial<Pick<CompanyPost, 'title' | 'dek' | 'hue' | 'kind' | 'category'>>;

/** the room a post's card is painted in */
export function cardRoom(post: Pick<CardPost, 'kind' | 'category'>): CoRoom {
  return roomForKind(post.kind ?? 'blog', post.category ?? null);
}

/** true for a room name the card route may be handed */
export function isCoRoom(room: string): room is CoRoom {
  return room in CO_ROOMS;
}

/**
 * The storage key of a post's card. A pure function of the post, so the page
 * and the route agree on it without talking: `card-v5-<slug>-<room>[-<nudge>]`.
 *
 * ⚠️ PLAIN CHARACTERS ONLY — no `%`, no `:`, no `#`. The first v4 key carried
 * `encodeURIComponent("blog:seed")`, which put a literal `%3A` INTO the key;
 * storage then normalised the object name to a colon, while the read side
 * percent-encoded the key once more and asked for `%253A`. Supabase answered
 * `400 InvalidKey` as JSON, the browser refused to paint JSON into an <img>
 * (ERR_BLOCKED_BY_ORB), the HEAD probe failed on the same address, and so
 * EVERY thumbnail was recomposed on EVERY request and then redirected to a
 * URL that could not be read. Blank cards, a second of function time each.
 * A key that survives `encodeURIComponent` unchanged cannot do that.
 */
export function cardKey(slug: string, room: CoRoom, seed: string): string {
  const safe = (s: string) => s.toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/^-+|-+$/g, '');
  // the seed is the slug, or the slug plus the composer's nudge (`slug#18`);
  // only the nudge needs to reach the key, the slug is already there.
  const nudge = seed.startsWith(slug) ? safe(seed.slice(slug.length)) : safe(seed);
  return `card-${CARD_VERSION}-${safe(slug)}-${room}${nudge ? `-${nudge}` : ''}`;
}

/** where the hosted card lives — null with no storage configured (local dev) */
export function hostedCardUrl(post: CardPost): string | null {
  return hostedHeroUrl(cardKey(post.slug, cardRoom(post), postSeed(post)));
}

/** the composing route — the fallback, and the address other systems (mail) use */
export function postCardUrl(post: CardPost, author?: CardAuthor | null): string {
  const q = new URLSearchParams({ slug: post.slug, title: (post.title ?? '').toLowerCase() });
  // WHICH ROOM this post is read in. The card is painted in the room's own
  // colour, shaded by the same seed the page shades itself by, so the picture
  // and the page it opens are the same place (law_portal, lib/brand/portal).
  // The generator only ever sees this url, never the row.
  q.set('room', cardRoom(post));
  // the card says what the post IS, in two small lines under its name.
  if (post.dek) q.set('dek', post.dek);
  // THE SHADE IS SEEDED BY THE SLUG, and two neighbouring slugs can land on
  // neighbouring shades — `week-2026-07-30` and `week-2026-09-03` came out
  // near enough to be mistaken for each other in the index. A post's own `hue`
  // column re-seeds the shade without touching the url the post lives at, so
  // a card can be moved off its neighbour from the composer.
  if (post.hue != null || postSeed(post) !== post.slug) q.set('seed', postSeed(post));
  if (author?.name) q.set('by', author.name.toLowerCase());
  if (author?.avatarUrl) q.set('face', author.avatarUrl);
  return `/api/brand/post-card?${q.toString()}`;
}

/**
 * Is this media item the post's OWN generated card?
 *
 * The weekly letter used to be filed with its thumbnail as `media[0]` — the
 * one picture the mail needed, stored on the row as if it were a photograph.
 * A picture whose only content is the post's own title is not article media
 * (law_no_picture_of_a_title): the reader rendered it inline under the
 * headline, and the index pushed it through the slow media path. A generated
 * card is a banner. It belongs on the index and in a link unfurl, never inside
 * the page, and it is never a reason to skip composing a fresh one.
 */
export function isGeneratedCard(m: CompanyMedia | string | null | undefined): boolean {
  const src = typeof m === 'string' ? m : m?.src;
  if (!src) return false;
  return src.includes('/api/brand/post-card') || src.includes(`/${'email-heroes'}/card-`);
}

/** a post's REAL media — photographs, screenshots, video; never its own card */
export function realMedia(post: Pick<CompanyPost, 'media'>): CompanyMedia[] {
  return (post.media ?? []).filter((m) => !isGeneratedCard(m));
}
